import {
    Box,
    Typography,
    TableContainer,
    Paper,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    alpha,
    Checkbox,
    IconButton,
    Button,
    Tooltip,
} from '@mui/material'
import { ISite } from '../interfaces'
import theme from '../../../../theme/theme'
import { findEntityById } from '../../../../utils/EntityUtils'
import { useAppSelector } from '../../../../redux/hook'
import { ReactElement, useMemo, useState } from 'react'
import { updateEntityInstancesStatus } from '../../../../services/entityServices'
import {
    EntityInstanceTypes,
    TemplatePermissionMode,
    UserPermissions,
} from '../../../../constants/constants'
import ConfirmationDialog from '../../../Common/ConfirmationDialog'
import EntityInstance from '../../../Entity/EntityInstance'
import { useTranslation } from 'react-i18next'

interface ISitesTable {
    siteList: ISite[]
    handleMarkerClick: (selectedSiteUID: string) => void
    reloadSiteList: () => void
}
export const SitesTable = (props: ISitesTable): ReactElement => {
    const { t } = useTranslation()
    const { siteList, handleMarkerClick, reloadSiteList } = props
    const entityTemplatesList = useAppSelector((state) => state.nav.navList)
    const fmSlice = useAppSelector((state) => state.facilityMonitoring)
    const { siteEntityTemplateId, siteBuildingAssetCount } = fmSlice

    const orgSlice = useAppSelector((state) => state.org)

    const { org, permissions } = orgSlice

    const [rowSelection, setRowSelection] = useState<string[]>([])
    const [rowsToDelete, setRowsToDelete] = useState<string[]>([])
    const [openDeleteDialog, setOpenDeleteDialog] = useState<boolean>(false)
    const [isEdit, setIsEdit] = useState<boolean>(false)
    const [entityData, setEntityData] = useState<{
        templateName: string
        templateId: string
        entityId: string
        orgId: string
    }>({
        templateName: '',
        templateId: '',
        entityId: '',
        orgId: '',
    })
    const [openEntityInstance, setOpenEntityInstance] = useState(false)

    const hasWritePermission = permissions.includes(
        UserPermissions.ENTITY_WRITER,
    )
    // selectedSite?.permissionLevel !== TemplatePermissionMode.READ &&

    const siteEntityTemplateIdName = findEntityById(
        entityTemplatesList,
        siteEntityTemplateId,
    ).name
    const siteName = siteEntityTemplateIdName
        ? t(siteEntityTemplateIdName)
        : t('Site')

    const deleteEntitiesHandler = () => {
        setRowsToDelete(rowSelection)
        setOpenDeleteDialog(true)
    }

    const handleDeleteDialogOperation = async () => {
        await updateEntityInstancesStatus(org?.id || '', rowsToDelete, 'delete')
        setRowSelection([])
        setRowsToDelete([])
        setOpenDeleteDialog(false)
        reloadSiteList()
    }

    const onClickCheckBox = (event: boolean, site: ISite) => {
        setRowSelection((prev) =>
            event ? [...prev, site.id] : prev.filter((id) => id !== site.id),
        )
    }

    const onClickEditIcon = (site: ISite) => {
        setOpenEntityInstance(true)
        setIsEdit(true)
        setEntityData({
            templateName: siteName,
            templateId: siteEntityTemplateId,
            entityId: site.id,
            orgId: org?.id || '',
        })
        reloadSiteList()
    }

    const handleCloseFields = (reload: boolean) => {
        setOpenEntityInstance(false)
        setEntityData({
            templateName: '',
            templateId: '',
            entityId: '',
            orgId: '',
        })
        if (reload) {
            reloadSiteList()
        }
    }

    const handleOpenFields = () => {
        setOpenEntityInstance(true)
        setIsEdit(false)
        setEntityData({
            templateName: siteName,
            templateId: siteEntityTemplateId,
            entityId: '',
            orgId: '',
        })
    }

    const disableDeleteButton = useMemo(() => {
        return Object.keys(rowSelection).length === 0
    }, [rowSelection])

    return (
        <Box
            sx={{
                width: '35%',
                padding: '0px 16px',
                height: '100%',
                overflowY: 'hidden',
            }}
        >
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between', // Pushes buttons to the right
                    marginBottom: 2,
                }}
            >
                <Typography
                    component="h3"
                    variant="h5"
                    sx={{
                        fontSize: '24px',
                        marginTop: 1,
                    }}
                >
                    {t('siteListTitle', { siteName })}
                </Typography>
                {hasWritePermission && (
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            marginTop: 1,
                        }}
                    >
                        <Button
                            id="fm-sites-button-delete"
                            onClick={() => deleteEntitiesHandler()}
                            disabled={disableDeleteButton}
                            sx={{
                                minWidth: '20px',
                                maxWidth: '20px',
                                marginLeft: '15px',
                                filter: disableDeleteButton
                                    ? 'grayscale(100%)'
                                    : 'none',
                                opacity: disableDeleteButton ? 0.5 : 1,
                            }}
                        >
                            <img
                                alt={t('deletesite')}
                                src={`/assets/images/icons/delete-icon.svg`}
                            />
                        </Button>
                        <Button
                            onClick={() => handleOpenFields()}
                            sx={{
                                minWidth: '13px',
                                maxWidth: '13px',
                                marginLeft: '30px',
                                minHeight: '13px',
                                maxHeight: '13px',
                                marginRight: '10px',
                            }}
                            id="add-group-button"
                        >
                            <img
                                alt={t('addnewsite')}
                                src={`/assets/images/icons/add-icon.svg`}
                            />
                        </Button>
                    </Box>
                )}
            </Box>

            <ConfirmationDialog
                openDialog={openDeleteDialog} // Controls visibility
                handleComfirm={handleDeleteDialogOperation} // Function to execute on confirm
                handleClose={() => setOpenDeleteDialog(false)} // Closes dialog
                DialogHeaderText={t('confirmDeleteHeader')}
                DialogBodyText={t('confirmDeleteMessage')}
            />

            {openEntityInstance && (
                <EntityInstance
                    isEdit={isEdit}
                    handleCloseFields={handleCloseFields}
                    entityData={entityData}
                    instanceType={EntityInstanceTypes.NORMAL}
                />
            )}
            <TableContainer
                component={Paper}
                sx={{ boxShadow: 'none', maxHeight: 'calc(100% - 48px)' }}
            >
                <Table
                    // stickyHeader //Enabling this is overriding the table header background color I set.
                    aria-label={t('stickytable')}
                    style={{ height: '100%' }}
                    // size="small"
                >
                    <TableHead
                        sx={{
                            backgroundColor: alpha(
                                theme.palette.primary.main,
                                0.21,
                            ),
                        }}
                    >
                        <TableRow>
                            {hasWritePermission && (
                                <>
                                    <TableCell></TableCell>
                                    <TableCell></TableCell>
                                </>
                            )}
                            <TableCell sx={{ paddingLeft: '10px' }}>
                                {t('nameColumn', { siteName })}
                            </TableCell>
                            <TableCell> {t('buildingsColumn')}</TableCell>
                            <TableCell>{t('assetsColumn')}</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody
                        style={{
                            height: 'calc(100% - 48px)',
                            overflow: 'scroll',
                        }}
                    >
                        {siteList.map((site, index) => {
                            const siteNode = siteBuildingAssetCount.find(
                                (node) => node.siteId === site.id,
                            )
                            const countOfBuildingsAtSite =
                                siteNode?.buildingCount ?? 0
                            const countOfAssets = siteNode?.assetCount ?? 0
                            const canEdit =
                                hasWritePermission &&
                                site?.permissionLevel !==
                                    TemplatePermissionMode.READ

                            return (
                                <TableRow key={site.id}>
                                    {canEdit && (
                                        <>
                                            <TableCell
                                                sx={{
                                                    padding: '0.5px',
                                                    width: '40px',
                                                }}
                                            >
                                                <Checkbox
                                                    id={`fm-sites-checkbox-row-${index}`}
                                                    size="small"
                                                    checked={rowSelection.includes(
                                                        site.id,
                                                    )}
                                                    onChange={(event) =>
                                                        onClickCheckBox(
                                                            event.target
                                                                .checked,
                                                            site,
                                                        )
                                                    }
                                                />
                                            </TableCell>
                                            <TableCell
                                                sx={{
                                                    padding: '0.5px',
                                                    width: '40px',
                                                }}
                                            >
                                                <IconButton
                                                    id={`fm-sites-edit-button-row-${index}`}
                                                    onClick={() =>
                                                        onClickEditIcon(site)
                                                    }
                                                    aria-haspopup="true"
                                                    key="edit"
                                                >
                                                    <img
                                                        src="/assets/images/icons/edit.svg"
                                                        alt={t(
                                                            'editsitedetails',
                                                        )}
                                                        style={{
                                                            width: 20,
                                                            height: 20,
                                                        }}
                                                    />
                                                </IconButton>
                                            </TableCell>
                                        </>
                                    )}
                                    <TableCell
                                        id={`fm-sites-site-name-link-row-${index}`}
                                        onClick={() => {
                                            handleMarkerClick(site.uid)
                                        }}
                                        sx={{
                                            cursor: 'pointer',
                                            textDecoration: 'underline',
                                            whiteSpace: 'nowrap',
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            maxWidth: '150px',
                                            paddingLeft: '10px',
                                        }}
                                    >
                                        <Tooltip
                                            title={site.siteName}
                                            arrow
                                            placement="top"
                                        >
                                            <span>{site.siteName}</span>
                                        </Tooltip>
                                    </TableCell>
                                    <TableCell>
                                        {countOfBuildingsAtSite}
                                    </TableCell>
                                    <TableCell>{countOfAssets}</TableCell>
                                </TableRow>
                            )
                        })}
                    </TableBody>
                </Table>
            </TableContainer>
        </Box>
    )
}
